<template>
  <div class="flex flex-col gap-4">
    <section
      class="rounded-lg border border-slate-200 bg-white p-3 shadow-[0_12px_30px_rgba(15,23,42,0.06)]"
    >
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div class="min-w-0">
          <p class="text-[24px] font-black leading-8 text-slate-950">
            Hướng dẫn sử dụng hệ thống
          </p>
          <p
            class="mt-2 max-w-4xl text-[15px] font-medium leading-6 text-slate-600"
          >
            Tài liệu thao tác nhanh theo từng chức năng trong menu. Mỗi mục có
            ảnh chụp màn hình thực tế để người dùng nhận diện đúng khu vực cần
            thao tác.
          </p>
        </div>
        <RouterLink
          to="/faq"
          class="inline-flex min-h-10 items-center gap-2 rounded-lg border border-teal-200 bg-teal-50 px-4 py-2 text-sm font-extrabold !text-teal-800 !no-underline transition hover:bg-teal-100"
        >
          <i class="pi pi-question-circle"></i>
          Xem FAQ
        </RouterLink>
      </div>
    </section>

    <section class="grid grid-cols-1 gap-4 xl:grid-cols-[280px_minmax(0,1fr)]">
      <aside
        class="sticky top-20 h-fit rounded-lg border border-slate-200 bg-white p-3 shadow-[0_10px_24px_rgba(15,23,42,0.05)]"
      >
        <div class="px-2 pb-2 text-xs font-black uppercase text-slate-500">
          Mục hướng dẫn
        </div>
        <a
          v-for="item in guideItems"
          :key="item.id"
          :href="`#${item.id}`"
          class="flex min-h-10 items-center gap-2 rounded-md px-2 py-2 text-sm font-bold !text-slate-700 !no-underline transition hover:bg-teal-50 hover:!text-teal-800"
        >
          <i class="pi text-[13px] text-teal-700" :class="item.icon"></i>
          <span class="min-w-0 truncate">{{ item.title }}</span>
        </a>
      </aside>

      <div class="flex min-w-0 flex-col gap-4">
        <section
          v-for="item in guideItems"
          :id="item.id"
          :key="item.id"
          class="scroll-mt-24 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-[0_12px_30px_rgba(15,23,42,0.06)]"
        >
          <div class="border-b border-slate-200 bg-slate-50 px-4 py-4">
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div class="min-w-0">
                <div class="flex items-center gap-2">
                  <span
                    class="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-teal-100 text-teal-800"
                  >
                    <i class="pi" :class="item.icon"></i>
                  </span>
                  <p
                    class="text-[18px] mb-0 font-black leading-6 text-slate-950"
                  >
                    {{ item.title }}
                  </p>
                </div>
                <p class="mt-2 text-sm font-medium leading-6 text-slate-600">
                  {{ item.description }}
                </p>
              </div>
              <RouterLink
                v-if="item.route"
                :to="item.route"
                class="inline-flex min-h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-extrabold !text-slate-700 !no-underline transition hover:border-teal-200 hover:bg-teal-50 hover:!text-teal-800"
              >
                <i class="pi pi-external-link"></i>
                Mở chức năng
              </RouterLink>
            </div>
          </div>

          <div
            class="grid grid-cols-1 gap-4 p-4 lg:grid-cols-[minmax(0,1fr)_360px]"
          >
            <div
              class="min-w-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-50"
            >
              <img
                :src="item.screenshot"
                :alt="`Ảnh chụp màn hình ${item.title}`"
                class="block w-full bg-white object-contain"
                loading="lazy"
              />
            </div>

            <div class="flex min-w-0 flex-col gap-4">
              <div>
                <p class="text-[14px] font-black uppercase text-slate-500">
                  Cách thao tác
                </p>
                <ol
                  class="mt-2 flex list-decimal flex-col gap-2 pl-5 text-sm font-medium leading-6 text-slate-700"
                >
                  <li v-for="step in item.steps" :key="step">
                    {{ step }}
                  </li>
                </ol>
              </div>

              <div v-if="item.notes.length">
                <p class="text-[14px] font-black uppercase text-slate-500">
                  Lưu ý
                </p>
                <ul
                  class="mt-2 flex list-disc flex-col gap-2 pl-5 text-sm font-medium leading-6 text-slate-700"
                >
                  <li v-for="note in item.notes" :key="note">
                    {{ note }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
type GuideItem = {
  id: string;
  title: string;
  description: string;
  route: string;
  icon: string;
  screenshot: string;
  steps: string[];
  notes: string[];
};

const shot = (name: string) => `/help-screenshots/${name}.png`;

export default {
  name: "HelpGuidePage",
  setup() {
    const guideItems: GuideItem[] = [
      {
        id: "login",
        title: "Đăng nhập hệ thống",
        description:
          "Màn hình xác thực tài khoản trước khi truy cập các chức năng quản trị.",
        route: "/login",
        icon: "pi-lock",
        screenshot: shot("login"),
        steps: [
          "Nhập tên đăng nhập được cấp.",
          "Nhập mật khẩu.",
          "Bấm Đăng nhập để vào hệ thống.",
        ],
        notes: [
          "Nếu đăng nhập thất bại, kiểm tra lại tên đăng nhập và mật khẩu.",
          "Khi phiên hết hạn, hệ thống tự quay về màn hình đăng nhập.",
        ],
      },
      {
        id: "send-message",
        title: "Gửi tin nhắn",
        description:
          "Gửi tin nhắn Zalo OA dạng văn bản thông thường đến nhiều nhóm người nhận.",
        route: "/sendmessage",
        icon: "pi-send",
        screenshot: shot("send-message"),
        steps: [
          "Chọn tab Gửi tin nhắn thông thường.",
          "Chọn phạm vi gửi: từng khách hàng, tất cả, giới tính, độ tuổi, nhóm hoặc tổ.",
          "Nhập nội dung tin nhắn.",
          "Bấm Gửi tin nhắn.",
        ],
        notes: [
          "Khi gửi theo từng khách hàng, phải chọn ít nhất một người nhận.",
        ],
      },
      {
        id: "send-message-template",
        title: "Gửi tin nhắn theo mẫu đính kèm",
        description:
          "Thiết kế và gửi tin nhắn Zalo OA dạng mẫu đính kèm có khung chính, hình ảnh, thành phần và nút hành động.",
        route: "/sendmessage",
        icon: "pi-send",
        screenshot: shot("send-message-template"),
        steps: [
          "Chọn tab Gửi tin nhắn theo mẫu đính kèm.",
          "Chọn phạm vi gửi: từng khách hàng, tất cả, giới tính, độ tuổi, nhóm hoặc tổ.",
          "Nhập tiêu đề, nội dung, đường dẫn hình ảnh và đường dẫn cho khung chính.",
          "Cấu hình các thành phần đính kèm và loại hành động nếu cần.",
          "Kiểm tra khung xem trước rồi bấm Gửi tin nhắn.",
        ],
        notes: [
          "Các trường trong khung chính bắt buộc phải nhập.",
          "Có thể chọn hình trực tiếp tại trang gửi tin nhắn theo mẫu đính kèm.",
        ],
      },
      {
        id: "upload-image",
        title: "Đăng hình ảnh",
        description:
          "Tải ảnh lên máy chủ để dùng trong tin nhắn mẫu và các khung nội dung có hình.",
        route: "/uploadImage",
        icon: "pi-upload",
        screenshot: shot("upload-image"),
        steps: [
          "Bấm Đính kèm tập tin.",
          "Chọn một hoặc nhiều ảnh JPG, JPEG hoặc PNG.",
          "Kiểm tra danh sách tệp đã chọn.",
          "Bấm Đăng hình để tải lên máy chủ.",
        ],
        notes: [
          "Ảnh khung chính khuyến nghị 360 x 232 px.",
          "Ảnh thành phần khuyến nghị 50 x 50 px.",
        ],
      },
      {
        id: "add-people",
        title: "Thêm dân cư vào tổ",
        description: "Nhập danh sách dân cư bằng file Excel theo mẫu.",
        route: "/addPeople",
        icon: "pi-file-excel",
        screenshot: shot("add-people"),
        steps: [
          "Bấm Tải danh sách mẫu để lấy file DanCu.xlsx.",
          "Điền dữ liệu dân cư vào file mẫu.",
          "Bấm Đính kèm tập tin và chọn file XLS/XLSX.",
          "Bấm Đăng danh sách để hệ thống xử lý.",
        ],
        notes: [
          "Nếu có hồ sơ lỗi, xem bảng lỗi, sửa file Excel rồi đăng lại.",
          "Không đổi cấu trúc cột của file mẫu nếu backend yêu cầu đúng định dạng.",
        ],
      },
      {
        id: "resident-list",
        title: "Danh sách dân cư trong tổ",
        description:
          "Xem, tìm kiếm, chỉnh sửa và xóa thông tin dân cư thuộc phạm vi tài khoản.",
        route: "/residentList",
        icon: "pi-bars",
        screenshot: shot("resident-list"),
        steps: [
          "Dùng ô tìm kiếm để lọc theo họ tên hoặc số điện thoại.",
          "Bấm Sửa tại dòng cần chỉnh thông tin.",
          "Chọn một hoặc nhiều dòng rồi bấm Xóa đã chọn nếu cần xóa hàng loạt.",
        ],
        notes: [
          "Các thao tác xóa đều cần xác nhận trong hộp thoại.",
          "Danh sách phụ thuộc quyền và token của tài khoản đang đăng nhập.",
        ],
      },
      {
        id: "manager-group",
        title: "Quản lý nhóm",
        description:
          "Tạo nhóm người nhận, thêm thành viên và xóa thành viên để phục vụ gửi tin theo nhóm.",
        route: "/managerGroup",
        icon: "pi-plus-circle",
        screenshot: shot("manager-group"),
        steps: [
          "Bấm Thêm nhóm để tạo nhóm mới.",
          "Mở rộng một nhóm để xem danh sách thành viên.",
          "Bấm Thêm thành viên, chọn người cần thêm rồi lưu.",
          "Dùng nút Xóa để xóa nhóm hoặc thành viên khỏi nhóm.",
        ],
        notes: [
          "Nên đặt tên nhóm rõ mục đích để dễ chọn khi gửi tin.",
          "Xóa nhóm có thể ảnh hưởng danh sách gửi tin theo nhóm.",
        ],
      },
      {
        id: "upload-document",
        title: "Gửi văn bản",
        description:
          "Tạo văn bản, đính kèm PDF và gửi thông báo đến người nhận.",
        route: "/uploadDocument",
        icon: "pi-pencil",
        screenshot: shot("upload-document"),
        steps: [
          "Nhập tiêu đề và nội dung văn bản.",
          "Bấm Chọn PDF và chọn một hoặc nhiều file PDF.",
          "Chọn nhóm tài khoản người nhận.",
          "Chọn tài khoản nhận và bấm Gửi thông báo.",
        ],
        notes: [
          "Chức năng chỉ chấp nhận PDF.",
          "Kiểm tra kỹ danh sách người nhận trước khi gửi.",
        ],
      },
      {
        id: "document-process",
        title: "Danh sách văn bản",
        description:
          "Người nhận xem văn bản được gửi đến, mở PDF và gửi nội dung trả lời xử lý.",
        route: "/documentProcessList",
        icon: "pi-file",
        screenshot: shot("document-process-list"),
        steps: [
          "Tìm kiếm hoặc xem danh sách văn bản.",
          "Bấm trạng thái Đã xử lý/Chưa xử lý để mở chi tiết.",
          "Xem hoặc tải file đính kèm.",
          "Nhập nội dung trả lời nếu văn bản chưa xử lý.",
        ],
        notes: [
          "Sau khi trả lời thành công, trạng thái xử lý sẽ được cập nhật.",
        ],
      },
      {
        id: "view-document",
        title: "Chi tiết văn bản",
        description:
          "Màn hình đọc nội dung văn bản, xem tài liệu đính kèm và gửi phản hồi.",
        route: "/viewDocument/1",
        icon: "pi-eye",
        screenshot: shot("view-document"),
        steps: [
          "Xem tiêu đề, người gửi, thời gian và nội dung.",
          "Bấm Xem để mở PDF trong cửa sổ xem.",
          "Bấm Tải để tải file về máy.",
          "Nhập nội dung phản hồi và bấm Gửi trả lời nếu còn chưa xử lý.",
        ],
        notes: [
          "Ảnh minh họa dùng mã văn bản mẫu; dữ liệu thực tế phụ thuộc văn bản được chọn.",
        ],
      },
      {
        id: "document-status",
        title: "Quản lý văn bản",
        description:
          "Theo dõi văn bản đã gửi và trạng thái xử lý của từng người nhận.",
        route: "/documentStatusList",
        icon: "pi-inbox",
        screenshot: shot("document-status-list"),
        steps: [
          "Xem danh sách văn bản đã gửi.",
          "Bấm Xem ở Chi tiết văn bản để đọc nội dung và file đính kèm.",
          "Bấm Xem ở Chi tiết xử lý để xem từng người nhận đã xử lý hay chưa.",
        ],
        notes: ["Tỷ lệ xử lý hiển thị theo dạng Đã xử lý/Tổng số."],
      },
      {
        id: "manager-feedback",
        title: "Quản lý nhận góp ý",
        description: "Cấu hình thành viên nhận thông tin góp ý của người dân.",
        route: "/addManagerFeedbackPage",
        icon: "pi-envelope",
        screenshot: shot("manager-feedback"),
        steps: [
          "Chọn thành viên trong danh sách.",
          "Bấm Thêm để đưa vào danh sách nhận góp ý.",
          "Bấm Xóa tại dòng thành viên nếu cần gỡ khỏi danh sách.",
        ],
        notes: [
          "Danh sách thành viên lấy từ dữ liệu khách hàng/tài khoản do backend trả về.",
        ],
      },
      {
        id: "feedback-list",
        title: "Danh sách góp ý",
        description:
          "Tra cứu nội dung góp ý theo khoảng thời gian và phân loại.",
        route: "/feedbackList",
        icon: "pi-comments",
        screenshot: shot("feedback-list"),
        steps: [
          "Chọn Từ thời gian và Đến thời gian.",
          "Chọn phân loại hoặc để Tất cả.",
          "Bấm Làm mới danh sách.",
          "Dùng ô tìm kiếm để lọc theo tên, số điện thoại hoặc nội dung.",
        ],
        notes: ["Có các nút nhanh: Hôm nay, 7 ngày gần nhất, Tháng này."],
      },
      {
        id: "emergency-list",
        title: "Danh sách khẩn cấp",
        description:
          "Tra cứu các nội dung khẩn cấp người dân gửi theo khoảng thời gian.",
        route: "/emergencyList",
        icon: "pi-exclamation-triangle",
        screenshot: shot("feedback-list"),
        steps: [
          "Chọn Từ thời gian và Đến thời gian cần tra cứu.",
          "Bấm Làm mới danh sách để tải dữ liệu trong khoảng thời gian đã chọn.",
          "Có thể bấm Hôm nay, 7 ngày gần nhất hoặc Tháng này để chọn nhanh khoảng thời gian và tải lại danh sách.",
          "Dùng ô tìm kiếm để lọc theo họ tên, số điện thoại hoặc nội dung khẩn cấp.",
          "Bấm tiêu đề cột để sắp xếp dữ liệu; danh sách mặc định ưu tiên nội dung mới nhất.",
        ],
        notes: [
          "Khoảng thời gian được gửi theo giờ địa phương, tránh lệch ngày khi tra cứu.",
          "Danh sách lấy nội dung từ dữ liệu Emergencies và hiển thị kèm thông tin người gửi nếu đã có trong hệ thống.",
        ],
      },
      {
        id: "full-calendar",
        title: "Quản lý lịch trực",
        description: "Phân công và theo dõi nhân viên trực theo lịch tháng.",
        route: "/fullCalendar",
        icon: "pi-calendar",
        screenshot: shot("full-calendar"),
        steps: [
          "Bấm vào một ngày trên lịch.",
          "Chọn thành viên cần phân công.",
          "Bấm Thêm để thêm vào lịch trực.",
          "Bấm Xóa tại danh sách nhân viên trực để gỡ phân công.",
        ],
        notes: [
          "Lịch sử dụng múi giờ Asia/Ho_Chi_Minh theo cấu hình hiện tại.",
        ],
      },
      {
        id: "register",
        title: "Đăng ký tài khoản",
        description: "Tạo tài khoản mới cho người dùng hệ thống.",
        route: "/register",
        icon: "pi-user-plus",
        screenshot: shot("register"),
        steps: [
          "Nhập tên đăng nhập, mật khẩu, họ tên và số điện thoại.",
          "Chọn quyền hạn phù hợp.",
          "Chọn phòng/tổ nếu quyền yêu cầu.",
          "Bấm Đăng ký.",
        ],
        notes: [
          "Mật khẩu tối thiểu 6 ký tự.",
          "Chức năng yêu cầu quyền quản trị.",
        ],
      },
      {
        id: "user-list",
        title: "Quản lý tài khoản",
        description:
          "Xem danh sách tài khoản, chỉnh sửa thông tin và xóa tài khoản.",
        route: "/userList",
        icon: "pi-users",
        screenshot: shot("user-list"),
        steps: [
          "Tìm tài khoản theo họ tên hoặc tên đăng nhập.",
          "Bấm Chỉnh sửa tại dòng tài khoản.",
          "Cập nhật thông tin hoặc mật khẩu mới.",
          "Bấm Cập nhật hoặc Xóa.",
        ],
        notes: [
          "Nếu không đổi mật khẩu, để trống trường mật khẩu.",
          "Chức năng yêu cầu quyền quản trị.",
        ],
      },
      {
        id: "user-update",
        title: "Cập nhật tài khoản",
        description: "Màn hình cập nhật chi tiết một tài khoản cụ thể.",
        route: "/userUpdate/1",
        icon: "pi-user-edit",
        screenshot: shot("user-update"),
        steps: [
          "Kiểm tra thông tin tài khoản hiện tại.",
          "Sửa họ tên, số điện thoại, quyền hạn hoặc tổ.",
          "Nhập mật khẩu mới nếu cần đổi.",
          "Bấm Cập nhật để lưu hoặc Xóa để xóa tài khoản.",
        ],
        notes: [
          "Ảnh minh họa dùng mã tài khoản mẫu; dữ liệu thực tế phụ thuộc tài khoản được chọn.",
        ],
      },
    ];

    return { guideItems };
  },
};
</script>
